import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import * as adminUserApi from "./api";
import type { AdminUserCriteria } from "./schema";

export type AdminUserMutation =
  | { readonly kind: "create"; readonly body: Schemas["CreateAdminRequest"] }
  | { readonly kind: "roles"; readonly adminUserId: number; readonly body: Schemas["ChangeAdminRolesRequest"] }
  | { readonly kind: "disable"; readonly adminUserId: number; readonly body: Schemas["DisableAdminRequest"] }
  | { readonly kind: "totp"; readonly adminUserId: number; readonly body: Schemas["ResetAdminTotpRequest"] };

export const adminUserKeys = {
  all: (epoch: number) => ["admin", epoch, "admin-users"] as const,
  lists: (epoch: number) => [...adminUserKeys.all(epoch), "list"] as const,
  list: (epoch: number, criteria: AdminUserCriteria) => [...adminUserKeys.lists(epoch), criteria] as const,
};

export function useAdminUsers(criteria: AdminUserCriteria) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: adminUserKeys.list(session.sessionEpoch, criteria),
    queryFn: ({ signal }) => adminUserApi.getAdminUsers(apiClient, criteria, signal),
    staleTime: 0,
  });
}

export function useAdminUserMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: AdminUserMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        if (input.kind === "create") return await adminUserApi.createAdminUser(apiClient, input.body, request.controller.signal);
        if (input.kind === "roles") return await adminUserApi.changeAdminRoles(apiClient, input.adminUserId, input.body, request.controller.signal);
        if (input.kind === "disable") return await adminUserApi.disableAdminUser(apiClient, input.adminUserId, input.body, request.controller.signal);
        return await adminUserApi.resetAdminTotp(apiClient, input.adminUserId, input.body, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, input) => {
      const actorId = session.actor?.id;
      if (input.kind !== "create" && input.adminUserId === actorId && (input.kind === "roles" || input.kind === "totp")) {
        sessionLifecycle.end("logout");
        return;
      }
      await queryClient.invalidateQueries({ queryKey: adminUserKeys.lists(session.sessionEpoch) });
    },
  });
}
